/**
 * GridCrash — Ghost Race Bar
 *
 * The in-game strip for an active ghost race: two lanes (you vs the
 * ghost) with progress fills, the ghost's live flag-score and final
 * target, and a result banner when the race settles. Rendered above
 * the board (inserted before .board-scroll-container), mirroring the
 * versus opponent panel's DOM shape but as its own feature module.
 *
 * Pure view — GhostRace drives it from main.ts.
 */

import type { GhostSnapshot, RaceOutcome } from "../game/ghost_race.js";

export interface RaceBarOptions {
  ghostName: string;
  ghostFinalScore: number;
  /** Solo races offer "Race again"; the one-shot daily doesn't. */
  retryable: boolean;
  onDismiss: () => void;
  onRaceAgain?: (() => void) | undefined;
}

let bar: HTMLElement | null = null;

export function showRaceBar(opts: RaceBarOptions): void {
  hideRaceBar();

  const container = document.querySelector(".game-container");
  const boardWrap = document.querySelector(".board-scroll-container");
  if (!container || !boardWrap) return;

  bar = document.createElement("div");
  bar.className = "race-bar";
  bar.setAttribute("role", "status");
  bar.setAttribute("aria-label", "Ghost race");
  bar.innerHTML = `
    <div class="race-bar__lanes">
      <div class="race-bar__lane">
        <span class="race-bar__name">🏁 YOU</span>
        <div class="race-bar__track"><div class="race-bar__fill race-bar__fill--you" style="width:0%"></div></div>
      </div>
      <div class="race-bar__lane">
        <span class="race-bar__name race-bar__name--ghost">👻 ${escapeHTML(opts.ghostName)}</span>
        <div class="race-bar__track"><div class="race-bar__fill race-bar__fill--ghost" style="width:0%"></div></div>
        <span class="race-bar__ghost-score">0 pts · chasing ${opts.ghostFinalScore}</span>
      </div>
    </div>
    <button class="race-bar__dismiss" aria-label="Dismiss ghost race">✕</button>
  `;

  bar.querySelector(".race-bar__dismiss")!.addEventListener("click", () => {
    hideRaceBar();
    opts.onDismiss();
  });

  // Stash for showRaceResult.
  raceOpts = opts;
  container.insertBefore(bar, boardWrap);
}

let raceOpts: RaceBarOptions | null = null;

export function updateRaceBar(playerProgress: number, ghost: GhostSnapshot): void {
  if (!bar) return;
  const you = bar.querySelector<HTMLElement>(".race-bar__fill--you");
  const gFill = bar.querySelector<HTMLElement>(".race-bar__fill--ghost");
  const gScore = bar.querySelector(".race-bar__ghost-score");
  if (you) you.style.width = `${Math.round(playerProgress * 100)}%`;
  if (gFill) {
    gFill.style.width = `${Math.round(ghost.progress * 100)}%`;
    gFill.classList.toggle("race-bar__fill--done", ghost.finished);
  }
  if (gScore && raceOpts) {
    gScore.textContent = ghost.finished
      ? `✅ finished · ${raceOpts.ghostFinalScore} pts`
      : `${ghost.liveScore} pts · chasing ${raceOpts.ghostFinalScore}`;
  }
}

export function showRaceResult(outcome: RaceOutcome, playerScore: number, ghostScore: number): void {
  if (!bar || !raceOpts) return;

  const message =
    outcome === "player" ? `🏆 You beat ${raceOpts.ghostName}! ${playerScore} vs ${ghostScore}`
    : outcome === "tie" ? `🤝 Dead heat with ${raceOpts.ghostName} — ${playerScore} pts`
    : outcome === "ghost" ? `👻 ${raceOpts.ghostName} holds on: ${ghostScore} vs ${playerScore}`
    : `💥 Detonated — ${raceOpts.ghostName}'s ${ghostScore} pts stands`;

  const banner = document.createElement("div");
  banner.className = `race-bar__result race-bar__result--${outcome === "player" ? "win" : "lose"}`;
  banner.textContent = message;

  if (raceOpts.retryable && raceOpts.onRaceAgain) {
    const again = document.createElement("button");
    again.className = "race-bar__again";
    again.textContent = "⟳ Race again";
    const cb = raceOpts.onRaceAgain;
    again.addEventListener("click", () => cb());
    banner.appendChild(again);
  }

  bar.querySelector(".race-bar__lanes")?.replaceWith(banner);
}

export function hideRaceBar(): void {
  bar?.remove();
  bar = null;
  raceOpts = null;
}

function escapeHTML(s: string): string {
  const div = document.createElement("div");
  div.textContent = s;
  return div.innerHTML;
}
